import React, { memo, useState, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useMemoizedFn } from 'ahooks';
import { Card } from 'tea-component';
// import Loading from '@gp/components/Loading';
import NoData from '@gp/components/NoData';
import Loading from '@gp/components/Loading';
import InfoBubble from '@gp/components/InfoBubble';
import DataTime from '@gp/components/DataTime';
// import RangePicker from '@gp/components/RangePicker';
import RangePicker from '@gp/components/GeneralRangePicker';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import StackBarChart from '@gp/pages/StudioDashboard/StudioOverall/components/StackBarChart';
import { reportOperationLog } from '@gp/services/global';
import {
  fetchTotalRevenueTrendsOfProjectsTabIntelligence,
  fetchTotalRevenueTrendsOfProjectsTabInternal,
  downloadTotalRevenueTrendsOfProjectsTabIntelligence,
  downloadTotalRevenueTrendsOfProjectsTabInternal,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import {
  getDownLoadCSVResponseHeaderFileName,
  downloadFileByBlob,
  stream2Object,
} from '@gp/utils/common';
import {
  getBarChartOptionData,
  legendFormatter,
  tootipFormatter,
  axisLabelOfXAxisFormatter,
  axisLabelOfYAxisFormatter,
} from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Projects/Details/components/Live/components/TotalRevenueTrends/utils';
import { FILE_TYPES2, FILE_TYPES_MAP } from '@gp/constants/const';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { PRECONDITION } from '../../constants';
import '@gp/pages/StudioDashboard/StudioOverall/Tabs/Projects/Details/components/Live/components/TotalRevenueTrends/index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';

const MAX_DIFF_MONTHS = 24;
// const RANGE_PICKER_TABS = [{ tabName: 'Month' }];
const RANGE_PICKER_TYPE = 'month';

const checkDateRangeDiff = (
  startDate: moment.Moment,
  endDate: moment.Moment,
  diff = MAX_DIFF_MONTHS,
  diffUnit = 'months',
): boolean => {
  let startDateTemp = moment.isMoment(startDate)
    ? startDate
    : moment(startDate);
  let endDateTemp = moment.isMoment(endDate) ? endDate : moment(endDate);
  // @ts-ignore
  return endDateTemp.diff(startDateTemp, diffUnit) <= diff;
};

interface ItotalRevenueTrendsProps extends IBaseProps {
  studioId: string;
  projects: IStudioOverallProject[];
  selectedProjects: IStudioOverallProject[];
  formModel: IStudioOverallProjectsDetailsLiveFilter;
  projectFilterLoading: boolean;
}

const TotalRevenueTrends: React.FC<ItotalRevenueTrendsProps> = (props) => {
  const {
    studioId,
    formModel,
    projects,
    selectedProjects,
    style,
    className,
    projectFilterLoading,
    ...remains
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInner, setIsLoadingInner] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // const [dateType, setDateType] = useState<IDateType>('Month');
  const [dateValue, setDateValue] = useState<string[]>([]);
  const [dateRangeTipsShow, setDateRangeTipsShow] = useState(false);
  const [totalRevenueTrends, setTotalRevenueTrends] = useState<any[]>([]);
  const [totalRevenueDataTime, setTotalRevenueDataTime] = useState<string>('');

  const isSelectInternal = useMemo(
    () => formModel?.source == PRECONDITION,
    [formModel?.source],
  );

  const isNoData = useMemo(() => {
    const flag1 = !totalRevenueTrends?.length || !selectedProjects?.length;
    const flag2 = totalRevenueTrends?.every((item) => {
      const a = Number.isNaN(Number.parseFloat(item?.target));
      const b = Number.isNaN(Number.parseFloat(item?.total_revenue));
      const c = item?.project_list?.every((subItem: any) => {
        return Number.isNaN(Number.parseFloat(subItem?.revenue));
      });
      return a && b && c;
    });
    return flag1 || flag2;
  }, [totalRevenueTrends, selectedProjects]);

  const downloadFile2Local = async (downloadData: any, fileType: number) => {
    // 拿到响应头中的文件名称
    const fileName = getDownLoadCSVResponseHeaderFileName(
      downloadData?.headers,
    );
    downloadFileByBlob(downloadData?.data, fileName);
    reportOperationLog(
      ALL_REPORT_OPERATION_LOG[
        fileType == FILE_TYPES_MAP.CSV
          ? 'STUDIO_OVERALL_DETAIL_PROJECTS_TOTAL_REVENUE_TRENDS_CSV_SUBMIT'
          : 'STUDIO_OVERALL_DETAIL_PROJECTS_TOTAL_REVENUE_TRENDS_EXCEL_SUBMIT'
      ],
      'button',
    );
  };

  const handleDownload = useMemoizedFn(async (payload) => {
    if (projectFilterLoading || !selectedProjects?.length) return;
    try {
      setIsDownloading(true);
      let res: any = null;
      if (isSelectInternal) {
        res = await downloadTotalRevenueTrendsOfProjectsTabInternal({
          studio_id: studioId,
          select_id: selectedProjects?.map((t) => t?.id),
          yoy: true,
          mom: true,
          start_date: dateValue?.[0],
          end_date: dateValue?.[1],
          file_type: Number.parseInt(payload?.fileType || FILE_TYPES_MAP.EXCEL),
        });
      } else {
        res = await downloadTotalRevenueTrendsOfProjectsTabIntelligence({
          studio_id: studioId,
          ids: selectedProjects || [],
          start_date: dateValue?.[0] ?? '',
          end_date: dateValue?.[1] ?? '',
          file_type: Number.parseInt(payload?.fileType || FILE_TYPES_MAP.EXCEL),
        });
      }
      downloadFile2Local(res, payload?.fileType || 2);
    } catch (err: any) {
      console.warn(err);
      const resData = await stream2Object(err?.data);
      throw new Error(resData?.msg || '');
    } finally {
      setIsDownloading(false);
    }
  });

  const handleDateChange = useMemoizedFn((startDate, endDate) => {
    let startDateTemp = moment(startDate).startOf('month');
    let endDateTemp = moment(endDate).startOf('month');
    // 结束时间保证在当前时间的月份之前，不能是当前时间所在月份 及 以后的月份
    endDateTemp = endDateTemp.isSameOrAfter(moment().startOf('month'))
      ? endDateTemp.subtract(1, 'months')
      : endDateTemp;
    const isRangeValid = checkDateRangeDiff(startDateTemp, endDateTemp);
    let newDateValue: string[] = [];
    // 时间范围跨度大于24个月，重置为 当前时间的上一个月开始，往前推24个月
    if (!isRangeValid) {
      startDateTemp = moment(endDateTemp).subtract(
        MAX_DIFF_MONTHS - 1,
        'months',
      );
      setDateRangeTipsShow(true);
      setTimeout(() => {
        setDateRangeTipsShow(false);
      }, 3000);
    }
    newDateValue = [
      startDateTemp?.format('YYYY-MM-DD'),
      endDateTemp?.format('YYYY-MM-DD'),
    ];
    setDateValue(newDateValue);
    updateTotalRevenueTrends(
      studioId,
      formModel,
      newDateValue,
      setIsLoadingInner,
    );
  });

  const updateTotalRevenueTrends = useMemoizedFn(
    async (
      studioId: string,
      formModel: IStudioOverallProjectsDetailsLiveFilter,
      dateValue: string[],
      isLoadingSetter = setIsLoading,
    ) => {
      if (projectFilterLoading || !selectedProjects?.length) return;
      try {
        isLoadingSetter?.(true);
        setTotalRevenueDataTime('');
        let res: any = null;
        if (isSelectInternal) {
          res = await fetchTotalRevenueTrendsOfProjectsTabInternal({
            studio_id: studioId,
            select_id: selectedProjects?.map((t) => t?.id),
            yoy: true,
            mom: true,
            start_date: dateValue?.[0],
            end_date: dateValue?.[1],
          });
        } else {
          res = await fetchTotalRevenueTrendsOfProjectsTabIntelligence({
            studio_id: studioId || '',
            ids: selectedProjects || [],
            start_date: dateValue?.[0] ?? '',
            end_date: dateValue?.[1] ?? '',
          });
        }
        const resData = res?.data;
        const startDate = resData?.req?.start_date;
        const endDate = resData?.req?.end_date;
        const newDateValue = startDate && endDate ? [startDate, endDate] : [];
        const trends = resData?.data?.list || [];
        const trendsDataTime = isSelectInternal ? resData?.data?.data_time : resData?.data?.insert_time;
        setTotalRevenueTrends(trends);
        setTotalRevenueDataTime(trendsDataTime || '');
        setDateValue(newDateValue);
      } catch (exception) {
        console.warn(exception);
        setTotalRevenueDataTime('');
      } finally {
        isLoadingSetter?.(false);
      }
    },
  );

  useEffect(() => {
    updateTotalRevenueTrends(studioId, formModel, dateValue, setIsLoading);
  }, [studioId, formModel?.projects, formModel?.source, projectFilterLoading]);

  return (
    <div
      {...remains}
      style={style}
      className={classnames(
        'dbg-studio-overall-details-projects-live-bar-chart-card',
        className,
      )}
    >
      <CardSlot
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            Total Revenue Trends
            {!!totalRevenueDataTime && <DataTime style={{ marginLeft: 6 }} dateType="Month" date={totalRevenueDataTime} />}
          </div>
        }
        rightDom={
          <>
            {!isLoading && !isLoadingInner && !isNoData && (
              <GeneralDownloadBtn
                uiType="2"
                fileTypes={FILE_TYPES2}
                isLoading={isDownloading}
                onConfirm={handleDownload}
              />
            )}
          </>
        }
      >
        {isLoading && <Loading loading style={{ height: 260 }} />}
        {isLoading || (
          <>
            {!!selectedProjects?.length && (
              <InfoBubble
                className="globalpedia-modal-font14"
                placementOffset={20}
                customBubbleContent={
                  dateRangeTipsShow
                    ? 'Can only query up to 24 months of data.'
                    : ''
                }
                DomOrText={
                  <>
                    {/* @ts-ignore */}
                    {/* <RangePicker
                      className="gp-common-range-picker"
                      range={true}
                      headerTabShow={false}
                      isAllOfTimeHidden={true}
                      tabOptions={RANGE_PICKER_TABS}
                      // disables={[]}
                      endYear={moment()
                        .subtract(1, 'months')
                        .format('YYYY-MM-DD')}
                      dateType={dateType}
                      setDateType={setDateType}
                      dateValue={dateValue}
                      setDateValue={setDateValue}
                      onChange={handleDateChange}
                    /> */}
                    <RangePicker
                      style={{ margin: '5px 0 20px' }}
                      type={RANGE_PICKER_TYPE}
                      typeList={[RANGE_PICKER_TYPE]}
                      value={dateValue}
                      onChange={(value: any, type: string) => {
                        handleDateChange(value?.[0], value?.[1]);
                      }}
                      // disabled={}
                      // dateRange={}
                    />
                  </>
                }
              />
            )}
            {isLoadingInner && (
              // <Loading loading loadingBg="#fff" style={{ height: 272 }} />
              <Loading loading style={{ height: 350, marginTop: 20 }} />
            )}
            {!isLoadingInner && isNoData && (
              <NoData height={350} constomStyle={{ fontSize: 14 }} />
            )}
            {!isLoadingInner && !isNoData && (
              <StackBarChart
                className="dbg-studio-overall-details-projects-live-bar-chart"
                data={totalRevenueTrends}
                getBarChartOptionData={getBarChartOptionData}
                legendFormatter={(id) => legendFormatter(projects, id)}
                tootipFormatter={(payload) =>
                  tootipFormatter(projects, totalRevenueTrends, payload)
                }
                axisLabelOfXAxisFormatter={axisLabelOfXAxisFormatter}
                axisLabelOfYAxisFormatter={axisLabelOfYAxisFormatter}
                tooltipConfig={{
                  // 如需要设置hover浮窗内，纵向滚动时，可设置该配置为true，即可开启hover浮窗交互
                  enterable: false,
                }}
              />
            )}
          </>
        )}
      </CardSlot>
    </div>
  );
};

export default memo(TotalRevenueTrends);
